import { client } from '../client';

class SanityAnalyticsService {
    constructor() {
        this.client = client;
    }

    // Create a new analytics event in Sanity
    async createAnalyticsEvent(eventData) {
        try {
            const {
                sessionId,
                category,
                action,
                label,
                value,
                url,
                userAgent,
                currentPage,
                timeOnPage,
                ipAddress,
                location,
                timestamp,
                consentGiven,
                sessionStartTime,
                sessionDuration
            } = eventData;

            // Map category to eventType for Sanity
            const eventTypeMap = {
                'page_view': 'page_view',
                'voice_interaction': 'voice_interaction',
                'ai_chat': 'ai_chat',
                'button_click': 'button_click',
                'navigation': 'navigation',
                'form_interaction': 'form_interaction',
                'session': 'session_start',
                'location': 'location',
                'consent': 'consent',
                'time_spent': 'time_spent',
                'scroll': 'scroll',
                'engagement': 'engagement'
            };

            const eventType = eventTypeMap[category] || 'engagement';

            // Prepare the document for Sanity
            const document = {
                _type: 'analytics',
                sessionId: sessionId,
                eventType: eventType,
                action: action,
                label: label,
                url: url,
                currentPage: currentPage,
                userAgent: userAgent,
                ipAddress: ipAddress,
                timestamp: timestamp || new Date().toISOString(),
                consentGiven: consentGiven || false,
                sessionStartTime: sessionStartTime,
                sessionDuration: sessionDuration
            };

            // Add value object if it exists
            if (value && typeof value === 'object') {
                document.value = {
                    commandLength: value.commandLength,
                    messageLength: value.messageLength,
                    responseLength: value.responseLength,
                    duration: value.duration,
                    isRecognized: value.isRecognized,
                    hasResponse: value.hasResponse,
                    timeOnPage: value.timeOnPage || timeOnPage,
                    scrollDepth: value.scrollDepth
                };
            }

            // Add location if it exists
            if (location) {
                document.location = {
                    country: location.country,
                    region: location.region,
                    city: location.city,
                    timezone: location.timezone
                };
            }

            // Create the document in Sanity
            const result = await this.client.create(document);
            console.log('📊 Analytics event saved to Sanity:', result._id);
            return result;

        } catch (error) {
            console.error('❌ Error saving analytics to Sanity:', error);
            throw error;
        }
    }

    // Get analytics data from Sanity
    async getAnalyticsData(options = {}) {
        try {
            const {
                limit = 100,
                offset = 0,
                eventType,
                startDate,
                endDate,
                sessionId
            } = options;

            let query = `*[_type == "analytics"]`;

            // Add filters
            const filters = [];
            
            if (eventType) {
                filters.push(`eventType == "${eventType}"`);
            }
            
            if (startDate) {
                filters.push(`timestamp >= "${startDate}"`);
            }
            
            if (endDate) {
                filters.push(`timestamp <= "${endDate}"`);
            }
            
            if (sessionId) {
                filters.push(`sessionId == "${sessionId}"`);
            }

            if (filters.length > 0) {
                query += `[${filters.join(' && ')}]`;
            }

            // Add ordering and pagination
            query += ` | order(timestamp desc) [${offset}...${offset + limit}] {
                _id,
                sessionId,
                eventType,
                action,
                label,
                value,
                url,
                currentPage,
                userAgent,
                ipAddress,
                location,
                timestamp,
                consentGiven,
                sessionStartTime,
                sessionDuration
            }`;

            const result = await this.client.fetch(query);
            console.log('📊 Retrieved analytics data from Sanity:', result.length, 'events');
            return result;

        } catch (error) {
            console.error('❌ Error fetching analytics from Sanity:', error);
            throw error;
        }
    }

    // Get analytics summary/statistics
    async getAnalyticsSummary(options = {}) {
        try {
            const { startDate, endDate } = options;

            let baseQuery = `*[_type == "analytics"]`;
            const filters = [];

            if (startDate) {
                filters.push(`timestamp >= "${startDate}"`);
            }
            
            if (endDate) {
                filters.push(`timestamp <= "${endDate}"`);
            }

            if (filters.length > 0) {
                baseQuery += `[${filters.join(' && ')}]`;
            }

            // Get total events
            const totalEventsQuery = `count(${baseQuery})`;
            const totalEvents = await this.client.fetch(totalEventsQuery);

            // Get events by type
            const eventsByTypeQuery = `${baseQuery} {
                eventType
            }`;
            const eventsByType = await this.client.fetch(eventsByTypeQuery);

            // Get unique sessions
            const uniqueSessionsQuery = `${baseQuery} {
                sessionId
            }`;
            const uniqueSessions = await this.client.fetch(uniqueSessionsQuery);

            // Get unique locations
            const uniqueLocationsQuery = `${baseQuery} {
                location
            }`;
            const uniqueLocations = await this.client.fetch(uniqueLocationsQuery);

            // Process the data
            const eventTypeCounts = {};
            eventsByType.forEach(event => {
                eventTypeCounts[event.eventType] = (eventTypeCounts[event.eventType] || 0) + 1;
            });

            const uniqueSessionIds = [...new Set(uniqueSessions.map(s => s.sessionId))];
            const uniqueLocationCount = uniqueLocations.filter(l => l.location).length;

            return {
                totalEvents,
                eventsByType: eventTypeCounts,
                uniqueSessions: uniqueSessionIds.length,
                uniqueLocations: uniqueLocationCount,
                dateRange: { startDate, endDate }
            };

        } catch (error) {
            console.error('❌ Error fetching analytics summary from Sanity:', error);
            throw error;
        }
    }

    // Get recent activity
    async getRecentActivity(limit = 20) {
        try {
            const query = `*[_type == "analytics"] | order(timestamp desc) [0...${limit}] {
                _id,
                eventType,
                action,
                label,
                timestamp,
                location,
                currentPage,
                value
            }`;

            const result = await this.client.fetch(query);
            console.log('📊 Retrieved recent activity from Sanity:', result.length, 'events');
            return result;

        } catch (error) {
            console.error('❌ Error fetching recent activity from Sanity:', error);
            throw error;
        }
    }

    // Delete old analytics data (for cleanup)
    async deleteOldAnalyticsData(daysOld = 30) {
        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - daysOld);

            const query = `*[_type == "analytics" && timestamp < "${cutoffDate.toISOString()}"]`;
            const oldEvents = await this.client.fetch(query);

            if (oldEvents.length > 0) {
                const ids = oldEvents.map(event => event._id);
                await this.client.delete(ids);
                console.log(`🗑️ Deleted ${oldEvents.length} old analytics events from Sanity`);
                return oldEvents.length;
            }

            return 0;

        } catch (error) {
            console.error('❌ Error deleting old analytics data from Sanity:', error);
            throw error;
        }
    }
}

// Create singleton instance
const sanityAnalyticsService = new SanityAnalyticsService();

export default sanityAnalyticsService; 